// src/pages/BonLivraisonDetail.tsx

import { useEffect, useState } from 'react';
import { createResource,getResources,deleteResource, url,updateResource} from '../../../router/api';
import toast from 'react-hot-toast';
import Header from '../../dashboard/components/Header';
import Sidebar from '../../dashboard/components/Sidebar';
import { useParams, useNavigate } from 'react-router-dom';

interface LigneBL {
  produit_reference: string;
  designation: string;
  quantite_commandee: number;
  quantite_livree: number;
  prix_unitaire_ht: number;
  total_ht: number;
}

interface DeliveryNoteDetail {
  id: number;
  numero: string;
  bon_commande_numero: string;
  date_livraison: string;
  statut: string;
  total_ttc: number;
  total_ht?: number;
  total_tva?: number;
  lignes: LigneBL[];
  client: {
    raison_sociale: string;
    email?: string;
    telephone?: string;
    adresse?: string;
  };
  preuve_reception_type: 'AUCUNE' | 'MANUSCRITE' | 'ELECTRONIQUE';
  preuve_reception_fichier: string | null;
  date_signature?: string | null;
  signe_par?: string | null;
  signe: boolean;
}

export default function BonLivraisonDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [bl, setBl] = useState<DeliveryNoteDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  /* ================= FETCH BON DE LIVRAISON ================= */
  useEffect(() => {
    if (!id) {
      toast.error('ID du bon de livraison manquant');
      navigate('/bons-livraison');
      return;
    }

    getResources(`bons-livraison/${id}`)
      .then((res) => {
        setBl(res.data.data || res.data);
      })
      .catch((err) => {
        console.error(err);
        toast.error('Impossible de charger le bon de livraison');
        navigate('/bons-livraison');
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  /* ================= FORMATAGE MONTANT ================= */
  const formatPrice = (amount: number | string) => {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(amount)) + ' GNF';
  };

  /* ================= TÉLÉCHARGEMENT PREUVE ================= */
  const handleDownloadProof = () => {
  if (!bl?.preuve_reception_fichier) return;

  const link = document.createElement('a');

  // ✅ utiliser l'URL telle quelle
  link.href = `${url}/storage/${bl.preuve_reception_fichier}`;

  // ✅ nom de fichier propre
  const extension = bl.preuve_reception_fichier.split('.').pop();
  link.download = `preuve-reception-${bl.numero}.${extension}`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};


  /* ================= ÉCRANS DE CHARGEMENT / ERREUR ================= */
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-xl text-gray-600 flex items-center gap-3">
          <i className="ri-loader-4-line animate-spin text-3xl"></i>
          Chargement du bon de livraison...
        </div>
      </div>
    );
  }

  if (!bl) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-xl text-red-600">Bon de livraison introuvable</div>
      </div>
    );
  }

  const totalHT = bl.total_ht || bl.total_ttc / 1.1925;
  const tvaMontant = bl.total_tva || bl.total_ttc - totalHT;

  return (
    
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:ml-64">
        <Header onMenuClick={() => setSidebarOpen(true)} />

        <main className="flex-1 overflow-y-auto pt-24 p-6">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">

            {/* Titre + Retour */}
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-4 mb-3">
                  <i className="ri-truck-line text-4xl text-green-600"></i>
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900">Bon de Livraison</h1>
                    <p className="text-xl text-gray-700 font-medium mt-1">N° {bl.numero}</p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-3 px-6 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition font-medium"
              >
                <i className="ri-arrow-left-line text-lg"></i>
                Retour
              </button>
            </div>

            {/* Infos générales */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                {/* Client */}
                <div>
                  <h3 className="font-semibold text-lg text-gray-800 mb-4 flex items-center gap-3">
                    <i className="ri-user-3-line text-xl text-green-600"></i>
                    Client
                  </h3>
                  <div className="space-y-2">
                    <p className="font-medium text-gray-900">{bl.client.raison_sociale}</p>
                    {bl.client.email && <p className="text-sm text-gray-600">Email : {bl.client.email}</p>}
                    {bl.client.telephone && <p className="text-sm text-gray-600">Tél : {bl.client.telephone}</p>}
                    {bl.client.adresse && <p className="text-sm text-gray-600">Adresse : {bl.client.adresse}</p>}
                  </div>
                </div>

                {/* Livraison */}
                <div>
                  <h3 className="font-semibold text-lg text-gray-800 mb-4 flex items-center gap-3">
                    <i className="ri-calendar-check-line text-xl text-green-600"></i>
                    Informations livraison
                  </h3>
                  <div className="space-y-2">
                    <p><strong>Date :</strong> {new Date(bl.date_livraison).toLocaleDateString('fr-FR')}</p>
                    <p><strong>Commande associée :</strong> {bl.bon_commande_numero}</p>
                    <p><strong>Statut :</strong>
                      <span className="ml-2 px-4 py-1 rounded-full bg-green-100 text-green-800 font-medium text-sm">
                        {bl.statut}
                      </span>
                    </p>
                  </div>
                </div>

                {/* Preuve de réception */}
                <div>
                  <h3 className="font-semibold text-lg text-gray-800 mb-4 flex items-center gap-3">
                    <i className="ri-file-check-line text-xl text-green-600"></i>
                    Preuve de réception
                  </h3>
                  {bl.preuve_reception_fichier ? (
                    <button
                      onClick={handleDownloadProof}
                      className="flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-medium shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                      title="Télécharger la preuve de réception"
                    >
                      <i className="ri-download-line text-xl"></i>
                      <span>Télécharger la preuve</span>
                    </button>
                  ) : (
                    <div className="flex items-center gap-2 text-gray-500 italic">
                      <i className="ri-error-warning-line text-lg"></i>
                      <span>Aucune preuve disponible</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Tableau des lignes */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-8 border-b border-gray-200 bg-gray-50">
                <h3 className="text-xl font-semibold text-gray-800">Articles livrés</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-green-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Référence</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Désignation</th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">Qté cmd</th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">Qté livrée</th>
                      <th className="px-6 py-4 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">P.U. HT</th>
                      <th className="px-6 py-4 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">Total HT</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {bl.lignes.map((ligne, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition">
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {ligne.produit_reference || '—'}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {ligne.designation}
                        </td>
                        <td className="px-6 py-4 text-center text-sm text-gray-700">
                          {ligne.quantite_commandee}
                        </td>
                        <td className="px-6 py-4 text-center text-sm font-bold text-green-700">
                          {ligne.quantite_livree}
                        </td>
                        <td className="px-6 py-4 text-right text-sm text-gray-700">
                          {formatPrice(ligne.prix_unitaire_ht)}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-semibold text-gray-900">
                          {formatPrice(ligne.total_ht)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totaux */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
              <div className="flex justify-end">
                <div className="w-full max-w-md space-y-4">
                  <div className="flex justify-between text-lg">
                    <span>Total HT</span>
                    <span className="font-semibold">{formatPrice(totalHT)}</span>
                  </div>
                  <div className="flex justify-between text-lg">
                    <span>TVA </span>
                    <span className="font-semibold">{formatPrice(tvaMontant)}</span>
                  </div>
                  <div className="flex justify-between text-2xl font-bold text-green-600 border-t-2 border-green-200 pt-4">
                    <span>Total TTC</span>
                    <span>{formatPrice(bl.total_ttc)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            {/* <div className="flex justify-end gap-4">
              <button className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-3">
                <i className="ri-printer-line"></i>
                Imprimer le bon de livraison
              </button>
            </div> */}

          </div>
        </main>
      </div>
    </div>
  );
}